// FormMetaRowActions — the Form meta & SQL row kebab. One item, Re-import, over
// POST exports/reimport-form-vertical-export.
//
// Renders NOTHING unless the form is connected: a form with no live export has
// nothing to re-import. `dbConnection` is the same state the row's Switch
// reports, so the kebab and the Switch can never disagree.
//
// Its own component rather than inline in the column's `render` because it owns
// the dropdown's open state, and hooks can't live in a render callback.
// `portal` is required — the table scrolls horizontally, so an in-flow panel
// would be clipped by that container.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';
import type { DataSetupForm } from '@/api/endpoints/dataSetupApi';

interface FormMetaRowActionsProps {
  form: DataSetupForm;
  /** Absent = hidden, the house convention for a verb-gated action. */
  onReimport?: (form: DataSetupForm) => void;
}

const FormMetaRowActions = ({ form, onReimport }: FormMetaRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Nothing to offer: no handler, or no export behind this row.
  if (!onReimport || !form.dbConnection) return null;

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={200}
      portal
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          size='sm'
          aria-haspopup='menu'
          aria-expanded={open}
          aria-label={t('data_setup_meta_row_actions')}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        />
      }
    >
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={(e) => {
          e.stopPropagation();
          setOpen(false);
          onReimport(form);
        }}
      >
        <Icon name='refresh-ccw' size={15} />
        <span className='app-dropdown__item-label'>{t('data_setup_meta_reimport')}</span>
      </button>
    </Dropdown>
  );
};

export default FormMetaRowActions;
